import gql from "graphql-tag";
import {
  contactMethodFields,
  deviceFields,
  organizationFields,
  incidentFields,
  profileFields,
  userFields,
} from "../../graphql/fragments";

export const ZONE_RESPONDERS = gql`
  query ZoneResponders($id: ID!) {
    organizationResponders(id: $id, includeAncestors: true) {
      id
      escalationLevel
      incidentTypes
      accessHours
      type
      status
      contactMethod {
        ...ContactMethodFields
      }
      organization {
        ...OrganizationFields
      }
      user {
        ...UserFields
      }
    }
  }
  ${contactMethodFields}
  ${organizationFields}
  ${userFields}
`;

export const UPDATE_SMART_ZONE = gql`
  mutation UpdateZone(
    $id: ID!
    $name: String!
    $timeZone: String!
    $profile: InputZoneProfile!
    $subtype: String!
  ) {
    updateZone(
      id: $id
      name: $name
      timeZone: $timeZone
      subtype: $subtype
      profile: $profile
    ) {
      ...OrganizationFields
      profile {
        ...ProfileFields
      }
    }
  }
  ${organizationFields}
  ${profileFields}
`;

export const EEN_EVENTS = gql`
  query eenEvents(
    $organizationId: String!
    $actor: String!
    $eventType: String!
  ) {
    eenEvents(
      organizationId: $organizationId
      actor: $actor
      eventType: $eventType
    ) {
      accountId
      actorId
      actorType
      creatorId
      endTimestamp
      id
      startTimestamp
      thumbnail
      type
    }
  }
`;

export const EEN_VIDEO_FEED = gql`
  query eenVideoFeed(
    $fromTimeStamp: String!
    $deviceId: String!
    $organizationId: String!
  ) {
    eenVideoFeed(
      fromTimestamp: $fromTimeStamp
      deviceId: $deviceId
      organizationId: $organizationId
    ) {
      video
    }
  }
`;

export const EEN_VIDEO_CAMERA_CONFIG = gql`
  query eenVideoCameraConfig($cameraId: String!, $organizationId: String!) {
    eenVideoCameraConfig(cameraId: $cameraId, organizationId: $organizationId) {
      typeOfEvents
    }
  }
`;

export const EEN_CAMERA_SETTINGS = gql`
  query eenCameraSettings($cameraId: String!, $organizationId: String!) {
    eenCameraSettings(cameraId: $cameraId, organizationId: $organizationId) {
      cloudRetentionDays
      eenPortalTimezone
      eenPreviewModeTransmit
      eenMainVideoModeTransmit
    }
  }
`;

export const GET_ALL_EEN_EVENTS = gql`
  query eenAllEvents(
    $organizationId: ID!
    $actor: String!
    $eventType: String!
    $pageToken: String
    $pageSize: Int
    $startTimestamp: String
    $endTimestamp: String
  ) {
    eenAllEvents(
      organizationId: $organizationId
      actor: $actor
      eventType: $eventType
      pageToken: $pageToken
      pageSize: $pageSize
      startTimestamp: $startTimestamp
      endTimestamp: $endTimestamp
    ) {
      results {
        id
        startTimestamp
        endTimestamp
        actorId
        type
        thumbnail
      }
      nextPageToken
      prevPageToken
    }
  }
`;

export const EEN_EVENT_IMAGES = gql`
  query eenEventImages($imageRequests: [ImageRequest]!) {
    eenEventImages(imageRequests: $imageRequests) {
      actorId
      organizationId
      startTimestamp
      thumbnail
    }
  }
`;

export const FACILITY_ZONE_STATS = gql`
  query FacilityZoneStats($facilityId: ID!) {
    facilityZoneStats(facilityId: $facilityId) {
      total
      zones
      mapped
      unmapped
    }
  }
`;

export const GET_ZONE_UNITS = gql`
  query GetZoneUnits($zoneId: ID!) {
    organizationDescendants(id: $zoneId, type: "zone", subtype: "unit") {
      id
      name
      subtype
      externalId
      unitType {
        id
        name
        width
        length
      }
      devices {
        id
        shortId
        type
        subtype
      }
    }
  }
`;

// All units in a facility (any depth). Unmapped units are those whose
// parentId equals the facility id; zone-mapped units' parentId is the zone.
export const GET_FACILITY_UNITS = gql`
  query GetFacilityUnits($facilityId: ID!) {
    organizationDescendants(
      id: $facilityId
      type: "zone"
      subtype: "unit"
      sort: "name"
    ) {
      id
      name
      parentId
      externalId
      parent {
        id
        name
        subtype
      }
      unitType {
        id
        name
        width
        length
      }
      devices {
        id
        shortId
        type
        subtype
      }
    }
  }
`;

// EEN video_camera devices at the facility level (unassigned to any zone/unit).
// Used in Create Smart Zone to populate the CAMERAS section.
export const GET_FACILITY_CAMERAS = gql`
  query GetFacilityCameras($facilityId: ID!) {
    organizationDevices(
      id: $facilityId
      deviceSubtypes: ["video_camera"]
      dispositions: ["in_service"]
    ) {
      ...DeviceFields
      organization {
        id
      }
    }
  }
  ${deviceFields}
`;

export const ASSIGN_UNITS_TO_ZONE = gql`
  mutation AssignUnitsToZone($zoneId: ID!, $unitIds: [ID!]!) {
    assignUnitsToZone(zoneId: $zoneId, unitIds: $unitIds) {
      id
      name
      subtype
      parentId
    }
  }
`;

export const VALIDATE_ZONE_VIDEO_TOKEN = gql`
  query ValidateZoneVideoToken($token: String!) {
    validateZoneVideoToken(token: $token) {
      actorId
      startTimestamp
      endedAt
      organizationId
      zoneName
      facilityName
      facilityPhone
    }
  }
`;

export const GET_ALL_ZONE_INCIDENTS = gql`
  query SmartZoneIncidents(
    $parentId: ID!
    $organizationSubtypes: [String]
    $dispositions: [String]
    $priorities: [String]
    $dateTimeFilter: DateTimeFilter
  ) {
    incidents(
      parentId: $parentId
      organizationSubtypes: $organizationSubtypes
      dispositions: $dispositions
      priority: $priorities
      dateTimeFilter: $dateTimeFilter
    ) {
      ...IncidentFields
      organization {
        id
        name
        parentId
        timeZone
        type
        subtype
        externalId
        ancestors(type: "facility") {
          id
          name
        }
        devices {
          type
          subtype
          id
          name
          externalId
          shortId
        }
      }
      tasks {
        id
        taskId
        description
        dueAt
        insertedAt
        priority
        resolution
        title
        updatedAt
        assignee {
          id
          firstName
          lastName
          username
        }
      }
      devices {
        location
      }
      events {
        data
        name
        timestamp
        type
      }
    }
  }
  ${incidentFields}
`;

export const GENERATE_ZONE_VIDEO_TOKEN = gql`
  mutation GenerateZoneVideoToken($incidentId: ID!, $zoneId: ID!) {
    generateZoneVideoToken(incidentId: $incidentId, zoneId: $zoneId) {
      token
    }
  }
`;
